import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.jsx';
import { QrCode } from '../components/QrCode.jsx';
import { useDashboard } from './dashboardContext.jsx';

const inputS =
  'bg-sidebar border border-rule-2 rounded-lg px-3.5 py-2.5 text-fg-2 text-lg font-mono text-center tracking-[6px] w-[200px] outline-none';

export function ProfilePage() {
  const { user } = useDashboard();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [msg, setMsg] = useState('');
  const cardCls = 'bg-card border border-rule rounded-2xl px-[26px] py-[22px] mb-4';
  const inputS =
    'bg-sidebar border border-rule-2 rounded-lg px-3.5 py-2.5 text-fg-2 text-lg font-mono text-center tracking-[6px] w-[200px] outline-none';
  const startSetup = async () => {
    const r = await api.post('/auth/2fa/setup');
    if (r) setSetup(r);
  };
  const enable2FA = async () => {
    if (code.length !== 6) return;
    const r = await api.post('/auth/2fa/enable', {
      secret: setup.secret,
      code,
    });
    if (r && r.enabled) {
      setMsg('2FA active avec succes! Reconnectez-vous.');
      setSetup(null);
      setCode('');
    } else setMsg('Code invalide, reessayez.');
  };
  const disable2FA = async () => {
    if (confirm('Desactiver la verification en deux etapes?')) {
      await api.post('/auth/2fa/disable');
      setMsg('2FA desactive.');
    }
  };
  // ─── Sessions ───
  const [sessions, setSessions] = useState(null);
  const loadSessions = useCallback(async () => {
    setSessions((await api.get('/auth/sessions')) || []);
  }, []);
  useEffect(() => {
    loadSessions();
  }, [loadSessions]);
  const revokeSession = async (sess) => {
    if (!confirm(sess.isCurrent ? 'Révoquer la session courante ? Vous serez déconnecté.' : 'Révoquer cette session ?'))
      return;
    const ok = await api.del('/auth/sessions/' + sess.id);
    if (ok) {
      if (sess.isCurrent) window.location.reload();
      else loadSessions();
    }
  };
  const fmtDt = (d) => (d ? new Date(d.endsWith('Z') ? d : d + 'Z').toLocaleString('fr-CA') : '');
  return (
    <div>
      <h1
        className="text-[22px] font-bold text-fg"
        style={{
          margin: '0 0 20px',
        }}
      >
        Mon profil
      </h1>
      <div className={cardCls}>
        <div className="text-sm font-bold text-blue-400 mb-3.5 uppercase tracking-widest">
          Verification en deux etapes (2FA)
        </div>
        {msg && (
          <div className="text-[13px] mb-3 p-2.5 rounded-lg text-ok bg-ok/10 border border-ok/20">
            {msg}
          </div>
        )}
        {user?.twoFactorEnabled && !setup && (
          <div>
            <div className="text-ok text-[13px] mb-3">2FA est actif sur votre compte.</div>
            {user?.twoFactorRequired ? (
              <div className="text-muted text-[12px]">
                La vérification en deux étapes est obligatoire : seul un administrateur peut la réinitialiser
                (téléphone perdu).
              </div>
            ) : (
              <button
                onClick={disable2FA}
                className="border-none rounded-lg py-2 px-[18px] text-white cursor-pointer text-xs font-semibold bg-crit hover:opacity-90"
              >
                Desactiver 2FA
              </button>
            )}
          </div>
        )}
        {!user?.twoFactorEnabled && !setup && (
          <div>
            <div className="text-muted text-[13px] mb-3">
              Protegez votre compte avec une appli d'authentification (Google Authenticator, Authy, etc.)
            </div>
            <button
              onClick={startSetup}
              className="bg-blue-500 border-none rounded-lg py-2 px-[18px] text-white cursor-pointer text-xs font-semibold"
            >
              Configurer 2FA
            </button>
          </div>
        )}
        {setup && (
          <div>
            <div className="text-fg text-[13px] mb-3">1. Scannez ce QR code avec votre appli:</div>
            <div className="text-center mb-4">
              <QrCode value={setup.qrUri} />
            </div>
            <div
              className="text-muted text-[11px] mb-1.5 text-center"
              style={{
                wordBreak: 'break-all',
              }}
            >
              {'Cle manuelle: '}
              <code className="text-violet-400">{setup.secret}</code>
            </div>
            <div className="text-fg text-[13px] mb-2">2. Entrez le code a 6 chiffres:</div>
            <div className="flex gap-3 items-center">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className={inputS}
                placeholder="000000"
                maxLength={6}
              />
              <button
                onClick={enable2FA}
                disabled={code.length !== 6}
                className={`border-none rounded-lg py-2.5 px-5 text-white text-[13px] font-semibold ${
                  code.length === 6 ? 'bg-ok cursor-pointer hover:opacity-90' : 'bg-rule cursor-default'
                }`}
              >
                Activer
              </button>
            </div>
          </div>
        )}
      </div>
      <div className={cardCls}>
        <div className="text-sm font-bold text-blue-400 mb-3.5 uppercase tracking-widest">Sessions</div>
        <div className="text-muted text-[12px] mb-3">
          Chaque connexion crée une session liée à son adresse IP (renouvelée par code 2FA toutes les 12 h).
        </div>
        {sessions === null && <div className="text-muted text-[13px]">Chargement...</div>}
        {sessions && sessions.length === 0 && <div className="text-muted text-[13px]">Aucune session active.</div>}
        {sessions &&
          sessions.map((sess) => (
            <div
              key={sess.id}
              className="flex items-center justify-between gap-3 py-2 border-t border-rule first:border-t-0 text-[12px]"
            >
              <div className="min-w-0">
                <div className="text-fg-2 font-mono">
                  {sess.ipAddress}
                  {sess.isCurrent && <span className="ml-2 text-ok font-sans">(cette session)</span>}
                </div>
                <div className="text-muted truncate" title={sess.userAgent}>
                  {sess.userAgent || 'Navigateur inconnu'}
                </div>
                <div className="text-muted-2">
                  {'Créée : ' + fmtDt(sess.createdAt) + ' · Dernière vérification : ' + fmtDt(sess.lastVerifiedAt)}
                </div>
              </div>
              <button
                onClick={() => revokeSession(sess)}
                className="border border-rule-3 rounded-md py-1 px-2.5 text-crit cursor-pointer text-[11px] shrink-0"
                style={{ background: 'none' }}
              >
                Révoquer
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}
